import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import CommonUtils from '../helpers/utils';
import ActivityManager from './ActivityManagerLoader';
import { APP_API_URL_TRACKING, APP_DEBUG } from '../environment';

const ActivityDataTracking = (function () {
  class ActivityDataTracking {
    constructor() {
      this.initial();
    }

    initial() {
      const urlParameters = CommonUtils.getQuerystring();
      this.modules = {
        exploration: 'exploration',
        fundamental: 'fundamental',
        virtuallab: 'virtual lab',
      };

      const assetGuid = urlParameters.assetGuid;

      if (CommonUtils.isIFrame()) {
        if (!assetGuid) {
          throw new Error('assetGuid is mandatory');
        }
      }

      this.requestData = {
        // Post Data
        category: 'interactives', // Required
        subcategory: this.modules[ActivityManager.moduleName], // the DE Defined Asset Type (exploration | fundamental | virtual lab)
        assetGuid: assetGuid || uuidv4(), // DE will pass the asset GUID as a URL param ?assetGuid=xxxxxx
        interactiveSessionID: uuidv4(), // UUID generated by the interactive to track an instance of a user’s interaction (xxxxxxxx-xxxx...)
      };

      this.startDate = new Date();

      this.elapseTime = 0;
      this.idleTime = 0;
      this.idleTimeLast = 0;
      this.idleTimeLimit = 30; // 30 seconds before start count idle time
      this.idleTimeCount = 0;

      this.idleTimeInterval = null;

      this.elapseTimeReporter = null;

      document.onclick = () => {
        this.idleTimeCount = 0;
      };
      document.onkeyup = () => {
        this.idleTimeCount = 0;
      };
    }

    setElapseTime() {
      var currentTime = new Date();
      this.elapseTime = currentTime - this.startDate;
    }

    getRequestData() {
      return {
        category: this.requestData.category,
        subcategory: this.requestData.subcategory,
        assetGuid: this.requestData.assetGuid,
        interactiveSessionID: this.requestData.interactiveSessionID,
      };
    }

    start() {
      this.elapseTime = 0;
      this.idleTime = 0;
      this.idleTimeLast = 0;
      this.idleTimeCount = 0;

      this.requestData.subcategory = this.modules[ActivityManager.moduleName];

      this.idleTimeInterval = setInterval(() => {
        this.idleTimeCount += 1;
        if (this.idleTimeCount >= this.idleTimeLimit) {
          this.idleTime += 1000;
        }
      }, 1000);

      this.elapseTimeReporter = setInterval(() => {
        this.timeSpentInteractive();
        this.amountIdleTime();
      }, 60000);
    }

    stop() {
      clearInterval(this.idleTimeInterval);
      clearInterval(this.elapseTimeReporter);

      this.timeSpentInteractive();
      this.amountIdleTime();
    }

    /**
     * Entered Interactive
     */
    enteredInteractive() {
      this.start();

      const data = this.getRequestData();
      data.target = 'entered interactive';
      this.send(data);
    }

    /**
     * Time spent in interactive
     */
    timeSpentInteractive() {
      const data = this.getRequestData();

      this.setElapseTime();

      data.target = 'time spent in interactive';
      data.elapsedTime = this.elapseTime;

      this.send(data);
    }

    /**
     * Amount of idle time (no inputs)
     */
    amountIdleTime() {
      if (this.idleTimeLast !== this.idleTime) {
        const data = this.getRequestData();

        data.target = 'amount of idle time';
        data.elapsedTime = this.idleTime;
        this.idleTimeLast = this.idleTime;

        this.send(data);
      }
    }

    /**
     * Explorations: Energy Transfer and the Water Cycle, Follow Your Food, The Inconstant Moon
     * Fraction of Correct Answers input first try (example: 4/7 correct answers on 1st try)
     * @param {string} score
     */
    correctAnswersInputFirstTry(drops) {
      const data = this.getRequestData();

      data.target = 'Correct Answers input first try';

      const drags = [];
      drops.forEach((drop) => {
        if (typeof drop.dragExpected === 'string') {
          drags.push(drop.drag);
        } else {
          drop.drag.forEach((dragItem) => {
            drags.push(dragItem);
          });
        }
      });

      const firsTrySuccess = drags.filter((drag) => {
        return drag.attempts === 1;
      });

      data.score = firsTrySuccess.length + '/' + drags.length;

      this.send(data);
    }

    /**
     * Explorations: Energy Transfer and the Water Cycle, Follow Your Food, The Inconstant Moon
     * 100% of Animation Played (user can X out of animation early)
     * @param {boolean} score
     */
    animationPlayed(fullPLayed) {
      const data = this.getRequestData();

      data.target = '100% of Animation Played';
      data.isCorrect = fullPLayed;

      this.send(data);
    }

    /**
     * Exploration: Changes of States
     * Number of experiments done (Start button pushed)
     *
     * Virtual Labs: Erosion – Here Today, Gone Tomorrow, How Does Your Garden Grow?
     * Number of Experiments (“Tests”) done
     * @param {string} score
     */
    experimentsDone(score) {
      const data = this.getRequestData();

      data.target = 'Number of experiments done';
      data.score = score;

      this.send(data);
    }

    /**
     * Exploration: Changes of States
     * Variables chosen for each experiment
     *
     * Virtual Labs: Erosion – Here Today, Gone Tomorrow, How Does Your Garden Grow?
     * Variables chosen for each experiment
     * @param {string} score
     */
    variablesChosenByExperiment(score) {
      const data = this.getRequestData();

      data.target = 'Variables chosen for each experiment';
      data.score = score;

      this.send(data);
    }

    /**
     * Exploration: Describing Motion
     * Correct Answers on 1st try to each question :
     * - fixed distance / which car has highest speed
     * - fixed time / which car has highest speed
     * @param {string} score
     * @param {string} quetion
     */
    correctAnswersOnFirstTry(score, question) {
      const data = this.getRequestData();

      if (question === 'fixed-distance') {
        data.target = 'fixed distance';
      } else {
        data.target = 'fixed time';
      }
      data.score = score;

      this.send(data);
    }

    /**
     * Virtual Labs: Erosion – Here Today, Gone Tomorrow, How Does Your Garden Grow?
     * Amount of time in each level (Level 1 / Level 2)
     * @param {string} level
     */
    amountOfTimeByLevel(level) {
      const data = this.getRequestData();

      if (level === 'level-1') {
        data.target = 'Amount of time in Level 1';
        // data.elapsedTime = ActivityManager.timers['virtual-labs']['level-2']['elapseTime']
        data.elapsedTime = 0;
      } else {
        data.target = 'Amount of time in Level 2';
        // data.elapsedTime = ActivityManager.timers['virtual-labs']['level-1']['elapseTime']
        data.elapsedTime = 0;
      }

      this.send(data);
    }

    /**
     * Virtual Labs: Erosion – Here Today, Gone Tomorrow, How Does Your Garden Grow?
     * Number of times Results cleared
     * @param {string} score
     */
    numberOfTimesResultsCleared(score) {
      const data = this.getRequestData();

      data.target = 'Number of times Results cleared';
      data.score = score;

      this.send(data);
    }

    /**
     * Fun-Damental: What’s the Matter?
     * Note: Tracking details is more value for Fun-Damentals, so including more granular tracking.
     * Note: Users can repeat sections. Data should be tracked for each repetition.
     *
     * Properties / Solids Section - When sorting balls by mass, how many wrong answers did user get? (0, 1, 2, etc)
     * Changing States Section – When sorting beakers by state (solid, gas, liquid), how many wrong answers did user get? (0, 1, 2, etc)
     * @param {string} score
     * @param {string} section
     */
    amountOfWrongAnswers(score, section) {
      const data = this.getRequestData();

      if (section === 'solids-section') {
        data.target = 'solids section';
      } else {
        data.target = 'states section';
      }

      data.score = score;

      this.send(data);
    }

    /**
     * Fun-Damental: What’s the Matter?
     * Note: Tracking details is more value for Fun-Damentals, so including more granular tracking.
     * Note: Users can repeat sections. Data should be tracked for each repetition.
     *
     * Properties / Liquids Section - User entered the correct mass (230 g) of the liquid alone.
     * (TBD if we need this one - if we add student input for this answer then we’ll track this.)
     * @param {boolean} isCorrect
     */
    enteredTheCorrectMass(isCorrect) {
      const data = this.getRequestData();

      data.target = 'liquids section';
      data.isCorrect = isCorrect;

      this.send(data);
    }

    /**
     * Fun-Damental: What’s the Matter?
     * Note: Tracking details is more value for Fun-Damentals, so including more granular tracking.
     * Note: Users can repeat sections. Data should be tracked for each repetition.
     *
     * Changing States Section – Correct answer on first try for all Qs “Where is the water now?”
     * - Liquid heated: CA is vapor
     * - Vapor cooled: CA is water condensation
     * - Liquid cooled: CA is ice tray
     * - Solid heated: CA is beaker
     * @param {boolean} isCorrect
     * @param {string} question
     */
    correctAnswerOnFirstTry(isCorrect, question) {
      const data = this.getRequestData();

      const states = {
        liquidHeated: 'Liquid heated',
        vaporCooled: 'Vapor cooled',
        liquidCooled: 'Liquid cooled',
        solidHeated: 'Solid heated',
      };

      data.target = states[question];
      data.isCorrect = isCorrect;

      this.send(data);
    }

    send(data) {
      if (APP_DEBUG) {
        console.log(data.target, data);
      } else {
        axios
          .post(APP_API_URL_TRACKING, data)
          .then(function () {})
          .catch(function (error) {
            throw new Error(error);
          });
      }
    }

    destroy() {
      this.initial();
    }
  }

  return ActivityDataTracking;
})();

const DataTracking = new ActivityDataTracking();

export default DataTracking;
